/**
 * useResolvedBlocks.ts
 *
 * Resolve an ordered list of block ids to Block objects — the Step-4
 * replacement for the "map order-array ids over a whole-workspace live
 * query" pattern (sidebar section lists, Recent, work-area pages).
 *
 * Semantics:
 *   • Store-first: each id is a point read from blockStore (the context
 *     mirror). Deleted blocks are filtered out.
 *   • Reactive at PAGE granularity: the result recomputes when any
 *     page/board block changes (via usePages), so title/status changes
 *     reflect — but never on content-block typing. Row-level content
 *     reactivity belongs to the row's own useBlock subscription.
 *   • Self-healing: ids missing from the store (cold start — the block
 *     lives only in IndexedDB) are bulk-read once and pushed into the
 *     context, whose dual-write lands them in the store. Ids IndexedDB
 *     doesn't have are remembered and never re-fetched.
 */

"use client";

import { useEffect, useMemo, useReducer, useRef } from "react";
import type { Block } from "@/types/block";
import { appDB } from "../db/client";
import { blockStore } from "../store/blockStore";
import { usePages } from "./useBlock";
import { useBlockActions } from "@/contexts/blockContext";

export function useResolvedBlocks(ids: readonly string[]): Block[] {
  const { upsertBlocks } = useBlockActions();
  const pages = usePages(); // page-slice reactivity for the memo below
  const [healTick, bumpHealTick] = useReducer((n: number) => n + 1, 0);
  const attemptedRef = useRef<Set<string>>(new Set());

  // Stable key so a caller passing a fresh array of the same ids doesn't
  // recompute or re-heal.
  const idsKey = ids.join("|");

  const resolved = useMemo(() => {
    const out: Block[] = [];
    for (const id of ids) {
      if (!id) continue;
      const block = blockStore.getBlock(id);
      if (block && block.status !== "deleted") out.push(block);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, pages, healTick]);

  useEffect(() => {
    const missing = ids.filter(
      (id) => id && !blockStore.getBlock(id) && !attemptedRef.current.has(id),
    );
    if (missing.length === 0) return;
    missing.forEach((id) => attemptedRef.current.add(id));

    let cancelled = false;
    (async () => {
      try {
        const records = await appDB.blocks.bulkGet(missing);
        if (cancelled) return;
        const found = records
          .filter((r): r is NonNullable<typeof r> => !!r && r.status !== "deleted")
          .map(({ isSync, localUpdatedAt, syncAttempts, syncError, ...blockData }) => blockData as Block)
          // Don't clobber anything that arrived while we awaited.
          .filter((b) => !blockStore.getBlock(b._id));
        if (found.length > 0) {
          await upsertBlocks(found); // one context write → dual-write → store
          if (!cancelled) bumpHealTick(); // covers non-page-like healed blocks
        }
      } catch {
        // IDB unavailable — resolve from the store only.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, upsertBlocks]);

  return resolved;
}
